import { computed, getCurrentScope, onScopeDispose, ref, watch } from 'vue';

import {
  emptyPaginationMeta,
  mergeUniqueRecords,
  normalizePaginationMeta,
} from './agentResourceHelpers';

export const useAgentMemories = ({
  assistantId,
  canManage,
  enabled,
  memoryForm,
  resetMemoryForm,
  api,
  notify,
  t,
  resourceErrors,
  readResourceError,
}) => {
  const records = ref([]);
  const meta = ref(emptyPaginationMeta());
  const isLoadingMore = ref(false);
  const isSaving = ref(false);
  const isDeleting = ref(false);
  const editingId = ref(null);
  const pendingDelete = ref(null);
  let listRequestId = 0;
  let mutationRequestId = 0;

  const totalCount = computed(() => Number(meta.value.total_count) || 0);
  const hasMore = computed(
    () => Number(meta.value.page) < Number(meta.value.total_pages)
  );
  const isAllowed = targetAssistantId =>
    Boolean(targetAssistantId && enabled.value && canManage.value);
  const isCurrentListRequest = (requestId, targetAssistantId) =>
    requestId === listRequestId && targetAssistantId === assistantId.value;
  const isCurrentMutation = (requestId, targetAssistantId) =>
    requestId === mutationRequestId && targetAssistantId === assistantId.value;

  const fetchMemories = async ({
    targetAssistantId = assistantId.value,
    page = 1,
    append = false,
  } = {}) => {
    listRequestId += 1;
    const currentRequestId = listRequestId;
    if (!isAllowed(targetAssistantId)) {
      if (isCurrentListRequest(currentRequestId, targetAssistantId)) {
        records.value = [];
        meta.value = emptyPaginationMeta();
      }
      return [];
    }

    if (append) isLoadingMore.value = true;
    try {
      const { data } = await api.memories({
        assistantId: targetAssistantId,
        page,
        perPage: 25,
      });
      const nextRecords = data.payload || [];
      if (!isCurrentListRequest(currentRequestId, targetAssistantId)) {
        return nextRecords;
      }

      records.value = append
        ? mergeUniqueRecords(records.value, nextRecords)
        : nextRecords;
      meta.value = normalizePaginationMeta(data.meta, {
        page,
        per_page: 25,
      });
      return nextRecords;
    } finally {
      if (isCurrentListRequest(currentRequestId, targetAssistantId)) {
        isLoadingMore.value = false;
      }
    }
  };

  const loadMore = async () => {
    if (!hasMore.value || isLoadingMore.value) return;
    const targetAssistantId = assistantId.value;
    const expectedRequestId = listRequestId + 1;
    try {
      await fetchMemories({
        targetAssistantId,
        page: Number(meta.value.page) + 1,
        append: true,
      });
      if (isCurrentListRequest(expectedRequestId, targetAssistantId)) {
        resourceErrors.memories = '';
      }
    } catch (error) {
      if (isCurrentListRequest(expectedRequestId, targetAssistantId)) {
        resourceErrors.memories = readResourceError(error);
      }
    }
  };

  const save = async () => {
    if (!memoryForm.content.trim()) {
      notify(t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_MEMORY_REQUIRED'));
      return false;
    }

    const targetAssistantId = assistantId.value;
    const targetMemoryId = editingId.value;
    mutationRequestId += 1;
    const currentRequestId = mutationRequestId;
    isSaving.value = true;
    try {
      const memory = {
        memory_type: memoryForm.memoryType,
        key: memoryForm.key,
        content: memoryForm.content,
      };
      if (targetMemoryId) {
        await api.updateMemory({
          assistantId: targetAssistantId,
          memoryId: targetMemoryId,
          memory,
        });
      } else {
        await api.createMemory({ assistantId: targetAssistantId, memory });
      }
      if (!isCurrentMutation(currentRequestId, targetAssistantId)) return false;

      const wasEditing = Boolean(targetMemoryId);
      editingId.value = null;
      resetMemoryForm();
      await fetchMemories({ targetAssistantId });
      if (!isCurrentMutation(currentRequestId, targetAssistantId)) return false;

      notify(
        wasEditing
          ? t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_MEMORY_UPDATED')
          : t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_MEMORY_ADDED')
      );
      return true;
    } catch (error) {
      if (!isCurrentMutation(currentRequestId, targetAssistantId)) return false;
      notify(
        error?.message || t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_SAVE_MEMORY_ERROR')
      );
      return false;
    } finally {
      if (isCurrentMutation(currentRequestId, targetAssistantId)) {
        isSaving.value = false;
      }
    }
  };

  const edit = memory => {
    if (!canManage.value) return;
    editingId.value = memory.id;
    Object.assign(memoryForm, {
      memoryType: memory.memory_type,
      key: memory.key || '',
      content: memory.content,
    });
  };

  const cancelEdit = () => {
    editingId.value = null;
    resetMemoryForm();
  };

  const requestDelete = memory => {
    if (canManage.value) pendingDelete.value = memory;
  };

  const cancelDelete = () => {
    if (!isDeleting.value) pendingDelete.value = null;
  };

  const deleteMemory = async () => {
    if (!canManage.value || !pendingDelete.value) return false;

    const targetAssistantId = assistantId.value;
    const targetMemory = pendingDelete.value;
    mutationRequestId += 1;
    const currentRequestId = mutationRequestId;
    isDeleting.value = true;
    try {
      await api.deleteMemory({
        assistantId: targetAssistantId,
        memoryId: targetMemory.id,
      });
      if (!isCurrentMutation(currentRequestId, targetAssistantId)) return false;

      if (editingId.value === targetMemory.id) cancelEdit();
      pendingDelete.value = null;
      await fetchMemories({ targetAssistantId });
      if (!isCurrentMutation(currentRequestId, targetAssistantId)) return false;

      notify(t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_MEMORY_DELETED'));
      return true;
    } catch (error) {
      if (!isCurrentMutation(currentRequestId, targetAssistantId)) return false;
      notify(
        error?.message ||
          t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_DELETE_MEMORY_ERROR')
      );
      return false;
    } finally {
      if (isCurrentMutation(currentRequestId, targetAssistantId)) {
        isDeleting.value = false;
      }
    }
  };

  const reset = ({ resetDraft = true } = {}) => {
    listRequestId += 1;
    mutationRequestId += 1;
    records.value = [];
    meta.value = emptyPaginationMeta();
    isLoadingMore.value = false;
    isSaving.value = false;
    isDeleting.value = false;
    editingId.value = null;
    pendingDelete.value = null;
    if (resetDraft) resetMemoryForm({ markSaved: false });
  };

  const stopAssistantWatch = watch(assistantId, () => reset());
  const dispose = () => {
    stopAssistantWatch();
    reset({ resetDraft: false });
  };
  if (getCurrentScope()) onScopeDispose(dispose);

  return {
    records,
    meta,
    totalCount,
    hasMore,
    isLoadingMore,
    isSaving,
    isDeleting,
    editingId,
    pendingDelete,
    fetchMemories,
    loadMore,
    save,
    edit,
    cancelEdit,
    requestDelete,
    cancelDelete,
    deleteMemory,
    reset,
    dispose,
  };
};
